/** PW editorial: progressive enhancement only; no libraries, tracking or autoplay. */
(() => {
  'use strict';
  if (!('customElements' in window)) return;

  class PWEditorialRail extends HTMLElement {
    connectedCallback() {
      this.disconnect();
      this.track = this.querySelector('[data-pw-rail]');
      this.controls = this.querySelector('[data-pw-controls]');
      this.previous = this.querySelector('[data-pw-prev]');
      this.next = this.querySelector('[data-pw-next]');
      if (!this.track || !this.controls || !this.previous || !this.next) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.previous.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.track.addEventListener('scroll', () => this.queueUpdate(), { ...options, passive: true });
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.move(event.key === 'ArrowRight' ? 1 : -1);
      }, options);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.queueUpdate());
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', () => this.queueUpdate(), { ...options, passive: true });
      }
      this.queueUpdate();
    }
    disconnectedCallback() { this.disconnect(); }
    disconnect() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
    }
    queueUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
    }
    update() {
      if (!this.isConnected || !this.track || !this.controls) return;
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      // Both directions are supported, including Safari's negative RTL scrollLeft.
      const position = Math.abs(this.track.scrollLeft);
      this.controls.hidden = maximum <= 2;
      this.previous.disabled = position <= 2;
      this.next.disabled = position >= maximum - 2;
    }
    move(direction) {
      const first = this.track.firstElementChild;
      if (!first) return;
      const computed = getComputedStyle(this.track);
      const step = first.getBoundingClientRect().width + (parseFloat(computed.columnGap) || 0);
      const rtl = computed.direction === 'rtl';
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({ left: direction * step * (rtl ? -1 : 1), behavior: reduced ? 'auto' : 'smooth' });
    }
  }

  class PWLookMedia extends HTMLElement {
    connectedCallback() {
      this.disconnect();
      this.video = this.querySelector('video');
      this.playButton = this.querySelector('[data-pw-play]');
      this.fallback = this.querySelector('[data-pw-video-fallback]');
      if (!this.video || !this.playButton) return;
      this.activated = Boolean(this.video.querySelector('source[src]'));
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.playButton.addEventListener('click', (event) => this.start(event), options);
      this.video.addEventListener('error', () => this.onError(), options);
      this.video.querySelectorAll('source').forEach((source) => {
        source.addEventListener('error', () => this.onError(), options);
      });
      this.video.addEventListener('play', () => {
        document.querySelectorAll('pw-look-media video').forEach((other) => {
          if (other !== this.video) other.pause();
        });
      }, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.video.pause();
      }, options);
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          if (!entries[0]?.isIntersecting) this.video.pause();
        }, { threshold: 0 });
        this.visibilityObserver.observe(this);
      }
    }
    disconnectedCallback() { this.disconnect(); }
    disconnect() {
      this.controller?.abort();
      this.visibilityObserver?.disconnect();
      this.video?.pause();
    }
    async start(event) {
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const source = this.video.querySelector('source[data-src]');
      if (!source || !source.dataset.src) return; // Keep the real link as a no-JS/error fallback.
      event.preventDefault();
      this.activated = true;
      if (!source.getAttribute('src')) {
        source.setAttribute('src', source.dataset.src);
        this.video.load();
      }
      this.video.hidden = false;
      this.playButton.hidden = true;
      if (this.fallback) this.fallback.hidden = true;
      try {
        await this.video.play();
        if (this.isConnected && !this.video.hidden) this.video.focus({ preventScroll: true });
      } catch (_error) {
        this.onError();
      }
    }
    onError() {
      if (!this.isConnected || !this.activated) return;
      this.video.hidden = true;
      this.playButton.hidden = true;
      if (this.fallback) { this.fallback.hidden = false; this.fallback.focus({ preventScroll: true }); }
      else this.playButton.hidden = false;
    }
  }

  if (!customElements.get('pw-editorial-rail')) customElements.define('pw-editorial-rail', PWEditorialRail);
  if (!customElements.get('pw-look-media')) customElements.define('pw-look-media', PWLookMedia);
})();
